import { 
    DataProvider, 
    RaRecord, 
    GetOneParams, 
    GetOneResult, 
    GetManyParams, 
    GetManyResult, 
    CreateParams, 
    CreateResult, 
    UpdateParams, 
    UpdateResult, 
    UpdateManyParams, 
    UpdateManyResult, 
    DeleteParams, 
    DeleteResult, 
    DeleteManyParams, 
    DeleteManyResult, 
    GetListParams,
    GetListResult,
    GetManyReferenceParams,
    GetManyReferenceResult
  } from 'react-admin';
  
  import MockLogs, { LogEntry } from './MockLogs'; // Import mock logs
  
  const LogsDataProvider: DataProvider = {
      getList: async <RecordType extends RaRecord = LogEntry>(
          resource: string,
          params: GetListParams
      ): Promise<GetListResult<RecordType>> => {
          console.log('Fetching logs with params:', params);

          const { page, perPage } = params.pagination || { page: 1, perPage: 10 };
          const { field, order } = params.sort || { field: 'timestamp', order: 'DESC' };

          // Sort the logs
          const sortedLogs = [...MockLogs].sort((a, b) => {
              const aValue = field === 'timestamp' ? new Date(a[field]) : a[field];
              const bValue = field === 'timestamp' ? new Date(b[field]) : b[field];
              const result = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
              return order === 'DESC' ? -result : result;
          });

          // Paginate the logs
          const start = (page - 1) * perPage;
          const end = start + perPage;
          const paginatedLogs = sortedLogs.slice(start, end);

          return {
              data: paginatedLogs as unknown as RecordType[], // Ensure correct typing with assertion
              total: MockLogs.length,
          };
      },

      getOne: async <RecordType extends RaRecord = LogEntry>(
          resource: string,
          params: GetOneParams
      ): Promise<GetOneResult<RecordType>> => {
          const record = MockLogs.find((log) => log.id === params.id);
          return new Promise((resolve) => setTimeout(() => resolve({ data: record as unknown as RecordType }), 500)
          );
      },

      getMany: async <RecordType extends RaRecord = LogEntry>(
          resource: string,
          params: GetManyParams
      ): Promise<GetManyResult<RecordType>> => {
          const records = MockLogs.filter((log) => params.ids.includes(log.id));
          return new Promise((resolve) => setTimeout(() => resolve({ data: records as unknown as RecordType[] }), 500)
          );
      },

      create: async <RecordType extends RaRecord = LogEntry>(
          resource: string,
          params: CreateParams
      ): Promise<CreateResult<RecordType>> => {
          const newRecord = { ...params.data, id: String(MockLogs.length + 1) };
          MockLogs.push(newRecord as LogEntry);
          return new Promise((resolve) => setTimeout(() => resolve({ data: newRecord as RecordType }), 500)
          );
      },

      update: async <RecordType extends RaRecord = LogEntry>(
          resource: string,
          params: UpdateParams
      ): Promise<UpdateResult<RecordType>> => {
          const index = MockLogs.findIndex((log) => log.id === params.id);
          if (index !== -1) {
              MockLogs[index] = { ...MockLogs[index], ...params.data } as LogEntry;
          }
          return new Promise((resolve) => setTimeout(() => resolve({ data: MockLogs[index] as unknown as RecordType }), 500)
          );
      },

      updateMany: async (
          resource: string,
          params: UpdateManyParams
      ): Promise<UpdateManyResult> => {
          const updatedIds = params.ids.map((id) => {
              const index = MockLogs.findIndex((log) => log.id === id);
              if (index !== -1) {
                  MockLogs[index] = { ...MockLogs[index], ...params.data } as LogEntry;
              }
              return id;
          });
          return new Promise((resolve) => setTimeout(() => resolve({ data: updatedIds }), 500)
          );
      },

      delete: async <RecordType extends RaRecord = LogEntry>(
          resource: string,
          params: DeleteParams
      ): Promise<DeleteResult<RecordType>> => {
          const index = MockLogs.findIndex((log) => log.id === params.id);
          const deletedRecord = MockLogs.splice(index, 1)[0];
          return new Promise((resolve) => setTimeout(() => resolve({ data: deletedRecord as unknown as RecordType }), 500)
          );
      },

      deleteMany: async (
          resource: string,
          params: DeleteManyParams
      ): Promise<DeleteManyResult> => {
          const deletedIds = params.ids.filter((id) => {
              const index = MockLogs.findIndex((log) => log.id === id);
              if (index !== -1) {
                  MockLogs.splice(index, 1);
                  return true;
              }
              return false;
          });
          return new Promise((resolve) => setTimeout(() => resolve({ data: deletedIds }), 500)
          );
      },
      getManyReference: function <RecordType extends RaRecord = any>(resource: string, params: GetManyReferenceParams): Promise<GetManyReferenceResult<RecordType>> {
          throw new Error('Function not implemented.');
      }
  };

  export const useLogDataProvider = () => LogsDataProvider;
  
  export default LogsDataProvider;